import { ReactNode } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors, Icon } from '@mercator-gcp/grout';

type SelectPillButtonVariant = 'default' | 'exclude';

const baseStyles = {
  padding: '8px 16px',
  borderRadius: '20px',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '1.5',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  outline: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  _active: {
    transform: 'scale(0.98)',
  },
};

const unselectedStyles = {
  border: '1px solid #E0E0E0',
  backgroundColor: Colors.White,
  color: '#000000',
  _hover: {
    borderColor: '#BDBDBD',
    backgroundColor: '#F5F5F5',
  },
};

const inclusionSelectedStyles = {
  border: `1px solid ${Colors.ForestGreen900}`,
  backgroundColor: Colors.ArmyGreen100,
  color: Colors.ForestGreen900,
  _hover: {
    borderColor: Colors.ForestGreen900,
    backgroundColor: Colors.ArmyGreen100,
  },
};

const exclusionSelectedStyles = {
  border: `1px solid ${Colors.Tangerine900}`,
  backgroundColor: '#FFF5F0',
  color: Colors.Tangerine900,
  _hover: {
    borderColor: Colors.Tangerine900,
    backgroundColor: '#FFF5F0',
  },
};

const getStateStyles = (isSelected: boolean, variant: SelectPillButtonVariant) => {
  if (!isSelected) return unselectedStyles;
  if (variant === 'exclude') return exclusionSelectedStyles;

  return inclusionSelectedStyles;
};

type SelectPillButtonProps = {
  isSelected: boolean;
  onClick: () => void;
  children: ReactNode;
  variant?: SelectPillButtonVariant;
};

const SelectPillButton = ({
  isSelected,
  onClick,
  children,
  variant = 'default',
}: SelectPillButtonProps) => {
  return (
    <Box
      as="button"
      type="button"
      sx={{ ...baseStyles, ...getStateStyles(isSelected, variant) }}
      onClick={onClick}
      aria-pressed={isSelected}
    >
      {isSelected && variant === 'exclude' && (
        <Icon name={Icon.Name.CloseX} size={16} fill={Colors.Tangerine900} />
      )}
      {isSelected && variant !== 'exclude' && (
        <Icon name={Icon.Name.Check} size={16} fill={Colors.ForestGreen900} />
      )}
      {children}
    </Box>
  );
};

export default SelectPillButton;
